import { createContext, useState, ReactNode, FC, useContext } from "react";

import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  PaletteMode,
} from "@mui/material";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import { styled } from "@mui/system";

import "../index.css";

const DARK_BACKGROUND = "#262626";
const LIGHT_BACKGROUND = "white";

interface DarkModeContextType {
  darkMode: boolean;
  toggleDarkMode: () => void;
}

const DarkModeContext = createContext<DarkModeContextType | undefined>(
  undefined
);

interface DarkModeProviderProps {
  children: ReactNode;
}

const getTheme = (mode: PaletteMode) =>
  createTheme({
    palette: {
      mode,
      primary: {
        main: mode === "dark" ? "#90caf9" : "#1976d2",
      },
      secondary: {
        main: mode === "dark" ? "#f48fb1" : "#d32f2f",
      },
      background: {
        default: mode === "dark" ? DARK_BACKGROUND : LIGHT_BACKGROUND, // Change default background color
        paper: mode === "dark" ? "#1e1e1e" : "#fff", // Change paper background color
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            "--background-main":
              mode === "dark" ? DARK_BACKGROUND : LIGHT_BACKGROUND,
            "--primary-color": mode === "dark" ? "#606060" : "#c7c7c7",
            "--primary-colorSwitched": mode === "dark" ? "#c7c7c7" : "#606060",
            "--secondary-color": mode === "dark" ? "#f48fb1" : "#d32f2f",
            "--naturGreen": mode === "dark" ? "#84c528" : "#0f8c32",
            "--naturGreenAlpha05": mode === "dark" ? "rgba(132,197,40,0.5)" : "rgba(15,140,50,0.5)",
            "--naturGreenSwitched": mode === "dark" ? "#0f8c32" : "#84c528",
            "--button-outlined-background":
              mode === "dark" ? "rgba(255,0,0,0)" : "#e1e1e1",
            "--red-button": mode === "dark" ? "#ff0000" : "#bb0000",
            "--font-primary": mode === "dark" ? "#f5f5f5" : DARK_BACKGROUND,
            "--font-primarySwitched": mode === "dark" ? DARK_BACKGROUND : "#f5f5f5" ,
            "--font-secondary": mode === "dark" ? "lightgray" : "(59, 59, 59)",
          },
        },
      },
    },
    // components: {
    //   MuiButton: {
    //     styleOverrides: {
    //       root: {
    //         fontSize: "1rem",
    //         borderRadius: "8px",
    //       },
    //     },
    //   },
    // },
  });

const DarkModeProvider: FC<DarkModeProviderProps> = ({ children }) => {
  const [darkMode, setDarkMode] = useState(true);
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const theme = getTheme(darkMode ? "dark" : "light");

  return (
    <DarkModeContext.Provider value={{ darkMode, toggleDarkMode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </DarkModeContext.Provider>
  );
};

const StyledBrightness7Icon = styled(Brightness7Icon)`
  font-size: 30px;
  transition: transform 0.2s ease; /* Add smooth transition */

  &:hover {
    transform: scale(1.1); /* Scale up by 10% on hover */
  }
`;

const StyledBrightness4Icon = styled(Brightness4Icon)`
  font-size: 30px;
  transition: transform 0.2s ease; /* Add smooth transition */

  &:hover {
    transform: scale(1.1); /* Scale up by 10% on hover */
  }
`;

const ColorModeIcon = () => {
  const darkModeContext = useContext(DarkModeContext);

  if (!darkModeContext) {
    return null; // or handle the error appropriately
  }

  const { darkMode, toggleDarkMode } = darkModeContext;

  return (
    <div className="color-mode-icon-container" onClick={toggleDarkMode}>
      {darkMode ? (
        <StyledBrightness7Icon className="color-mode-icon" />
      ) : (
        <StyledBrightness4Icon className="color-mode-icon" />
      )}
    </div>
  );
};

export { DarkModeContext, DarkModeProvider, ColorModeIcon };
